/**
 * Generic renderer for built-in property forms
 * Handles all field types, custom fields, ordering, and hiding
 */

import React, { useMemo } from 'react';
import { Images } from 'lucide-react';
import PhotoCapture from '../PhotoCapture';
import DynamicFieldRenderer from './DynamicFieldRenderer';
import {
  BuiltInFormSchema,
  BuiltInFieldConfig,
  PhotoFieldSchema,
  DynamicFormRendererProps,
  shouldShowConditionalField,
  isPhotoField,
  BuiltInFormSection
} from '../../types/builtInFormSchema';
import { FieldSchema } from '../../types/formSchema';

const BuiltInFormRenderer: React.FC<DynamicFormRendererProps> = ({
  schema,
  formData,
  photos,
  onFormDataChange,
  onPhotosChange,
  onSubmit,
  customFields = [],
  hiddenFields = [],
  hiddenSections = [],
  sectionOrder = [],
  builtInFieldOrder = [],
  navigationButtons,
  onGalleryOpen,
  selectedGallery,
  className = '',
  formTitle,
  formIcon
}) => {
  // Styling constants
  const commonSelectClassName = "w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400";
  const photoButtonClass = "w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 transition-all duration-200";
  const photoCaptureContainerClass = "p-4 border border-gray-200 rounded-lg bg-gray-50/30 space-y-2";
  const fieldWrapperClass = "space-y-2";
  const labelClass = "text-sm font-medium text-gray-700 flex items-center";
  const requiredMarker = <span className="text-red-500 mr-1">*</span>;
  const booleanButtonClass = (isSelected: boolean | null, actualValue: boolean) =>
    `flex-1 py-3 rounded-lg border-2 ${isSelected === actualValue ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors font-medium`;

  // Helper to check if field is hidden
  const isFieldHidden = (fieldId: string) => hiddenFields.includes(fieldId);

  // Merge built-in and custom sections
  const allSections = useMemo(() => {
    let sections: Array<BuiltInFormSection & { isCustom?: boolean; customFieldSchemas?: FieldSchema[] }> = [...schema.sections];
    
    // Add custom sections
    if (customFields && customFields.length > 0) {
      const customSectionMap: Record<string, FieldSchema[]> = {};
      
      // Group custom fields by section
      customFields.forEach(field => {
        const sectionId = field.section || 'custom_fields_unsectioned';
        if (!customSectionMap[sectionId]) {
          customSectionMap[sectionId] = [];
        }
        customSectionMap[sectionId].push(field);
      });
      
      // Convert to section format
      Object.entries(customSectionMap).forEach(([sectionId, fields]) => {
        sections.push({
          id: sectionId,
          title: sectionId === 'custom_fields_unsectioned' 
            ? 'Additional Fields' 
            : sectionId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          fields: fields.map(f => ({ id: f.id, type: f.type, label: f.label } as any)),
          isCustom: true,
          customFieldSchemas: fields
        });
      });
    }
    
    // Apply section ordering
    if (sectionOrder && sectionOrder.length > 0) {
      sections.sort((a, b) => {
        const aIndex = sectionOrder.indexOf(a.id);
        const bIndex = sectionOrder.indexOf(b.id);
        
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return 0;
      });
    }
    
    // Apply field ordering within built-in sections
    if (builtInFieldOrder && builtInFieldOrder.length > 0) {
      sections.forEach(section => {
        if (!section.isCustom) {
          section.fields.sort((a, b) => {
            const aIndex = builtInFieldOrder.indexOf(a.id);
            const bIndex = builtInFieldOrder.indexOf(b.id);
            
            if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
            if (aIndex !== -1) return -1;
            if (bIndex !== -1) return 1;
            return 0;
          });
        }
      });
    }
    
    return sections;
  }, [schema.sections, customFields, sectionOrder, builtInFieldOrder]);

  // Render photo preview
  const renderPhotoPreview = (photoDatas: any[], photoFieldId: string, altText: string) => {
    if (!photoDatas || photoDatas.length === 0) return null;
    
    const latestPhoto = photoDatas[photoDatas.length - 1];
    const imageUrl = latestPhoto?.url || latestPhoto?.dataUrl;
    
    if (!imageUrl) return null;
    
    return (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          if (onGalleryOpen) onGalleryOpen(photoFieldId);
        }}
        className="relative w-20 h-20 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:scale-105 ring-2 ring-slate-300 hover:ring-blue-400 flex-shrink-0 mt-2"
      >
        <img 
          src={imageUrl} 
          alt={altText} 
          className="w-full h-full object-cover"
        />
        <div className="absolute top-1 right-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
          <Images size={12} />
          {photoDatas.length}
        </div>
      </button>
    );
  };

  // Render a photo field
  const renderPhotoField = (field: PhotoFieldSchema) => {
    const photoData = photos[field.id] || [];
    
    return (
      <div key={field.id} className={photoCaptureContainerClass}>
        <label className={labelClass}>
          {field.required && requiredMarker}
          {field.label}
          {field.helpText && <span className="text-xs text-gray-500 ml-1">({field.helpText})</span>}
        </label>
        <PhotoCapture
          onPhotosCaptured={(newPhotos: any[]) => onPhotosChange(field.id, newPhotos as any)}
          maxPhotos={field.maxPhotos || 999}
          buttonLabel={`Capture ${field.label}`}
          buttonClass={photoButtonClass}
        />
        {renderPhotoPreview(photoData, field.id, field.label)}
      </div>
    );
  };

  // Render a text field
  const renderTextField = (field: any) => {
    return (
      <div key={field.id} className={fieldWrapperClass}>
        <label htmlFor={field.id} className={labelClass}>
          {field.required && requiredMarker}
          {field.label}
        </label>
        <input
          type="text"
          id={field.id}
          name={field.id}
          value={formData[field.id] || ''}
          onChange={(e) => onFormDataChange(field.id, e.target.value)}
          placeholder={field.placeholder}
          maxLength={field.maxLength}
          className={commonSelectClassName}
          required={field.required}
        />
      </div>
    );
  };

  // Render a textarea field
  const renderTextAreaField = (field: any) => {
    return (
      <div key={field.id} className={fieldWrapperClass}>
        <label htmlFor={field.id} className={labelClass}>
          {field.required && requiredMarker}
          {field.label}
        </label>
        <textarea
          id={field.id}
          name={field.id}
          value={formData[field.id] || ''}
          onChange={(e) => onFormDataChange(field.id, e.target.value)}
          rows={field.rows || 3}
          placeholder={field.placeholder}
          className={commonSelectClassName}
          required={field.required}
        />
      </div>
    );
  };

  // Render a dropdown field
  const renderDropdownField = (field: any) => {
    return (
      <div key={field.id} className={fieldWrapperClass}>
        <label htmlFor={field.id} className={labelClass}>
          {field.required && requiredMarker}
          {field.label}
        </label>
        <select
          id={field.id}
          name={field.id}
          value={formData[field.id] || ''}
          onChange={(e) => onFormDataChange(field.id, e.target.value)}
          className={commonSelectClassName}
          required={field.required}
        >
          {field.options?.map((opt: any) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    );
  };

  // Render a boolean field
  const renderBooleanField = (field: any) => {
    const value = formData[field.id];
    
    return (
      <div key={field.id} className={fieldWrapperClass}>
        <label className={labelClass}>
          {field.required && requiredMarker}
          {field.label}
        </label>
        <div className="flex space-x-4 mt-2">
          <button
            type="button"
            onClick={() => onFormDataChange(field.id, true)}
            className={booleanButtonClass(value, true)}
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => onFormDataChange(field.id, false)}
            className={booleanButtonClass(value, false)}
          >
            No
          </button>
          {field.style === 'yes-no-na' && (
            <button
              type="button"
              onClick={() => onFormDataChange(field.id, null)}
              className={booleanButtonClass(value, null as any)}
            >
              N/A
            </button>
          )}
        </div>
      </div>
    );
  };

  // Render a number field
  const renderNumberField = (field: any) => {
    return (
      <div key={field.id} className={fieldWrapperClass}>
        <label htmlFor={field.id} className={labelClass}>
          {field.required && requiredMarker}
          {field.label}
          {field.unit && <span className="text-xs text-gray-500 ml-1">({field.unit})</span>}
        </label>
        {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
        <input
          type="number"
          id={field.id}
          name={field.id}
          value={formData[field.id] || ''}
          onChange={(e) => onFormDataChange(field.id, parseFloat(e.target.value))}
          min={field.min}
          max={field.max}
          step={field.step}
          placeholder={field.placeholder}
          className={commonSelectClassName}
          required={field.required}
        />
      </div>
    );
  };

  // Render a date field
  const renderDateField = (field: any) => {
    return (
      <div key={field.id} className={fieldWrapperClass}>
        <label htmlFor={field.id} className={labelClass}>
          {field.required && requiredMarker}
          {field.label}
        </label>
        <input
          type="date"
          id={field.id}
          name={field.id}
          value={formData[field.id] || ''}
          onChange={(e) => onFormDataChange(field.id, e.target.value)}
          className={commonSelectClassName}
          required={field.required}
        />
      </div>
    );
  };

  // Render a single field based on type
  const renderField = (field: BuiltInFieldConfig) => {
    // Check if hidden
    if (isFieldHidden(field.id)) return null;
    
    // Check conditional rendering
    if (!shouldShowConditionalField(field, formData)) return null;
    
    // Render based on type
    switch (field.type) {
      case 'photo':
      case 'image': // Support both 'photo' (built-in) and 'image' (custom field) types
        return renderPhotoField(field as PhotoFieldSchema);
      case 'text':
        return renderTextField(field);
      case 'textarea':
        return renderTextAreaField(field);
      case 'dropdown':
        return renderDropdownField(field);
      case 'boolean':
        return renderBooleanField(field);
      case 'number':
        return renderNumberField(field);
      case 'date':
        return renderDateField(field);
      default:
        return null;
    }
  };

  return (
    <div className={`bg-white/80 backdrop-blur-sm p-6 sm:p-8 rounded-xl shadow-lg border border-yellow-200 min-h-[calc(100vh-12rem)] flex flex-col ${className}`}>
      {/* Form Title */}
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 border-b-2 border-gray-200 pb-4 mb-6 flex items-center gap-3">
        {formIcon}
        {formTitle || schema.formTitle}
      </h2>

      {/* Form */}
      <form onSubmit={onSubmit} className="space-y-6 flex-1 flex flex-col">
        <div className="flex-1 space-y-6">
          {/* Render all sections (built-in + custom) */}
          {allSections.map(section => {
            const visibleFields = section.fields.filter(f => {
              const fieldId = typeof f === 'string' ? f : f.id;
              return !hiddenFields.includes(fieldId);
            });
            if (visibleFields.length === 0) return null;
            if (hiddenSections.includes(section.id)) return null;
            
            return (
              <fieldset key={section.id} className="border border-gray-300 p-4 rounded-lg space-y-4">
                <legend className="text-md font-semibold text-gray-700 px-2">{section.title}</legend>
                {section.description && (
                  <p className="text-sm text-gray-600 -mt-2">{section.description}</p>
                )}
                {visibleFields.map(field => {
                  // Get field ID - either it's a string or an object with id property
                  const fieldId = typeof field === 'string' ? field : field.id;
                  
                  // Check if this is a custom field
                  if (section.isCustom && section.customFieldSchemas) {
                    const customFieldSchema = section.customFieldSchemas.find(f => f.id === fieldId);
                    if (customFieldSchema) {
                      return (
                        <DynamicFieldRenderer
                          key={fieldId}
                          field={customFieldSchema}
                          value={formData[fieldId]}
                          onChange={(value) => onFormDataChange(fieldId, value)}
                          formData={formData}
                          onFormDataChange={onFormDataChange}
                        />
                      );
                    }
                  }
                  
                  // Built-in field - field is a BuiltInFieldConfig object
                  return renderField(field);
                })}
              </fieldset>
            );
          })}
        </div>

        {/* Navigation Buttons */}
        {navigationButtons && (
          <div className="mt-auto pt-6 border-t border-gray-200 flex flex-col sm:flex-row gap-3 sm:justify-between">
            {navigationButtons.previous && (
              <button
                type="button"
                onClick={navigationButtons.previous.onClick}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02]"
              >
                {navigationButtons.previous.label}
              </button>
            )}
            {navigationButtons.next && (
              <button
                type="submit"
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 font-bold rounded-lg shadow-lg hover:from-yellow-500 hover:to-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02]"
              >
                {navigationButtons.next.label}
              </button>
            )}
          </div>
        )}
      </form>
    </div>
  );
};

export default BuiltInFormRenderer;
